import { useEffect } from "react";

/**
 * Keep one layer (and its GeoJSON source) on a mapbox-gl map in sync with
 * `data` — a symbol layer by default, or `type: "circle"` for the section
 * dots (which need a zoom range WigMap can't give either).
 *
 * ESCAPE HATCH: WigMap's GeoJSON layers forward `paint` only — no `layout`
 * (so no `text-field`), no `minzoom` / `maxzoom`. Text and badge layers are
 * therefore added to the map instance directly, the same way the section
 * labels already were. Library follow-up: expose `layout`, `minzoom`,
 * `maxzoom` (and `filter`) on WigMapGeoJsonLayer, then move these into the
 * declarative `layers` array.
 *
 * `layout` / `paint` / zoom range are applied when the layer is first added
 * (use data-driven expressions for anything that varies); `data` is pushed
 * with setData on every change. Empty data removes the layer.
 *
 * @param {{ current: object|null }} mapRef  mapbox-gl map instance ref
 * @param {boolean} mapReady
 * @param {{ id: string, data: object|null, type?: "symbol"|"circle",
 *   minzoom?: number, maxzoom?: number, layout?: object, paint?: object }} spec
 */
const useMapSymbolLayer = (mapRef, mapReady, spec) => {
  const { id, data } = spec;
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;
    const sourceId = `${id}-source`;
    const apply = () => {
      if (!map.getStyle || !map.getStyle()) throw new Error("style not ready");
      if (!data || !Array.isArray(data.features) || data.features.length === 0) {
        if (map.getLayer(id)) map.removeLayer(id);
        if (map.getSource(sourceId)) map.removeSource(sourceId);
        return;
      }
      const existing = map.getSource(sourceId);
      if (existing) {
        existing.setData(data);
        return;
      }
      map.addSource(sourceId, { type: "geojson", data });
      map.addLayer({
        id,
        type: spec.type || "symbol",
        source: sourceId,
        ...(spec.minzoom != null ? { minzoom: spec.minzoom } : {}),
        ...(spec.maxzoom != null ? { maxzoom: spec.maxzoom } : {}),
        layout: spec.layout || {},
        paint: spec.paint || {},
      });
    };
    // Not `isStyleLoaded()` + `once("load")`: isStyleLoaded is also false
    // while any source is still fetching, and "load" fires once per map, so
    // data arriving mid-fetch after first load was never added. Try now; if
    // the style isn't ready, retry on each "idle" (fires after every render)
    // until it sticks.
    const tryApply = () => {
      try {
        apply();
        return true;
      } catch {
        return false; // "Style is not done loading"
      }
    };
    if (tryApply()) return undefined;
    const onIdle = () => {
      if (tryApply()) map.off("idle", onIdle);
    };
    map.on("idle", onIdle);
    return () => {
      try {
        map.off("idle", onIdle);
      } catch {
        // map may already be removed
      }
    };
    // spec layout/paint are add-time only (see above)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, mapReady, id]);
};

export default useMapSymbolLayer;
